import { useEffect, useState } from 'react'
import axios from 'axios'
import useIsMounted from 'hooks/utils/useIsMounted'
import { useAuthedUser } from 'context/AuthedUser/AuthedUserContext'
import { CLIENT_THEME_VARIANT } from 'hooks/useThemeStyling/useThemeStyling'

export type TFilterOption = {
    id: number
    name?: string
} & Record<string, unknown>

interface ICaseBuilderIssuerVM {
    currentStep: number
    setCurrentStep: (step: number) => void
    accountNumberList: TFilterOption[]
    disputeReasonList: TFilterOption[]
    currencyList: TFilterOption[]
    productServiceList: TFilterOption[]
    typeList: TFilterOption[]
    fileTemplateList: TFilterOption[]
    isLoadingCurrencyList: boolean
    isSubmittingFinal: boolean
    handleFinalStep: () => void
}

const useCaseBuilderIssuerVM = (): ICaseBuilderIssuerVM => {
    const { isMounted } = useIsMounted()
    const { user } = useAuthedUser()
    const [currentStep, setCurrentStep] = useState<number>(1)
    const [currencyList, setCurrencyList] = useState<TFilterOption[]>([])
    const [isLoadingCurrencyList, setIsLoadingCurrencyList] = useState<boolean>(
        false
    )
    // const [isLoading, setIsLoading] = useState<boolean>(false)
    const [isSubmittingFinal, setIsSubmittingFinal] = useState<boolean>(false)
    // const [enableSaveAndContinue, setEnableSaveAndContinue] = useState<boolean>(
    //     true
    // )

    const clientThemeVariantId =
        user?.theme_variant ?? CLIENT_THEME_VARIANT.DISPUTE_PRO.id

    // 3: Lastminute, 6: Amadeus, and 7: Sabre
    const filteredProductServiceList = [
        CLIENT_THEME_VARIANT.LASTMINUTE.id,
        CLIENT_THEME_VARIANT.AMADEUS.id,
        CLIENT_THEME_VARIANT.SABRE.id,
        CLIENT_THEME_VARIANT.SOFI.id,
    ].includes(clientThemeVariantId)
        ? productServiceList.filter((item) => [1, 2, 4, 7].includes(item.id))
        : productServiceList

    const fileTemplateList: TFilterOption[] = [
        {
            name: 'Issuer Doc template A.pdf',
            id: 213,
            file: {
                id: 1973,
                filename: 'Issuer Doc template A.pdf',
                s3_key: 'templates/1/5fe67f8726ed5e91486e2a99cef8d5aa.pdf',
                content_type: 'application/pdf',
                size: 24753,
                date_created: '2024-07-23T19:45:53+00:00',
            },
        },
        {
            name: 'Issuer Doc template B.pdf',
            id: 225,
            file: {
                id: 1985,
                filename: 'Issuer Doc template B.pdf',
                s3_key: 'templates/1/5fe67f8726ed5e91486e2a99cef8d5aa.pdf',
                content_type: 'application/pdf',
                size: 24753,
                date_created: '2024-07-24T14:38:22+00:00',
            },
        },
    ]

    const handleFinalStep = () => {
        setIsSubmittingFinal(true)
        new Promise((resolve) => {
            setTimeout(() => {
                resolve({ message: '' })
            }, 1000)
        }).finally(() => {
            setIsSubmittingFinal(false)
        })
    }

    useEffect(() => {
        if (currentStep === 2) {
            setIsLoadingCurrencyList(true)
            axios
                .get('/cm/gen/currencies', {
                    params: {
                        limit: 999,
                    },
                })
                .then(({ data: { data } }) => {
                    if (!isMounted.current) return

                    setCurrencyList(
                        data.map((row: any) => {
                            return {
                                id: row.id,
                                value: row.id,
                                name: row.currency,
                            }
                        })
                    )
                })
                .finally(() => setIsLoadingCurrencyList(false))
        }
    }, [isMounted, currentStep])

    return {
        currentStep,
        setCurrentStep,
        accountNumberList,
        disputeReasonList,
        currencyList,
        productServiceList: filteredProductServiceList,
        typeList,
        fileTemplateList,
        isLoadingCurrencyList,
        isSubmittingFinal,
        handleFinalStep,
    }
}

const accountNumberList: TFilterOption[] = [
    { id: 1, name: '218912912869' },
    { id: 2, name: '165169812867' },
    { id: 3, name: '959815128731' },
]

export const disputeReasonList: TFilterOption[] = [
    { id: 1, name: 'Unauthorized' },
    { id: 2, name: 'Lost or stolen' },
    { id: 3, name: 'Service not provided' },
    { id: 4, name: 'Previously cancelled' },
    { id: 5, name: 'Paid by other means' },
    { id: 6, name: 'Duplicate' },
    { id: 7, name: 'Defective' },
    { id: 7, name: 'Not as described' },
]

const productServiceList: TFilterOption[] = [
    { id: 1, name: 'Airline Service' },
    { id: 2, name: 'Car Service' },
    { id: 3, name: 'Event Ticket' },
    { id: 4, name: 'Hotel Service' },
    { id: 5, name: 'Other Digital' },
    { id: 6, name: 'Other Physical' },
    { id: 7, name: 'Other Service' },
]
const typeList: TFilterOption[] = [
    { id: 1, name: 'PNR Locator' },
    { id: 2, name: 'Ticket Number' },
    { id: 3, name: 'Folio Number' },
    { id: 4, name: 'Booking Number' },
    { id: 5, name: 'RA Number' },
    { id: 6, name: 'Order Number' },
    { id: 7, name: 'Internal Reference Number' },
]

export default useCaseBuilderIssuerVM
